import * as Actions from '../actions/invoiceActions';
import Invoice from '../../entities/invoice';
import * as idb from '../../lib/idbd';
import locals from '../../lib/localStorage';
import ArrayMap from '../../lib/ArrayMap';
import ArrayMapRegistry from '../../lib/ArrayMapRegistry';
import {ensure} from '../../lib/picos/ensure';
import {isRecoverableError} from '../../lib/recoverableErrors';

export type InvoiceAction = { 
    type: string; 
    invoice: Invoice | null; 
    invoiceId: string | Invoice; 
    partial: boolean; 
    lastFetch: number; 
    invoices: Invoice[]; 
    err: any; 
    status: number; 
    message: string; 
}

let logging: boolean = locals.getBoolean(locals.REDUX_LOGGING, false);

/**
 * Call the storage worker.
 *
 * @param {ArrayMap<Invoice> | null} data
 * @param {string} firmId
 * @param {string} userId
 * @param {Invoice[]} [updates]
 */
const callStorageWorker = (data: ArrayMap<Invoice> | null, firmId: string, userId: string, updates?: Invoice[]) => {
	window.storageWorker
		  .updateStorage(firmId, userId, 'invoice', 'invoices', data?.values() || null, updates)
		  .catch(console.error);
}

/**
 * Update storage in backend.
 *
 * @param {ArrayMap<Invoice>} data
 * @param {Invoice[]} [updates]
 * @returns {ArrayMap<Invoice>}
 */
function updateStorage(data: ArrayMap<Invoice>, updates?: Invoice[]): ArrayMap<Invoice> {
	if (logging) console.debug('⮂ invoiceReducer: updateStorage');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length === 0) return data;
	else if (updates) callStorageWorker(null, firmId, userId, updates);
	else callStorageWorker(data, firmId, userId);

	return data;
}

/**
 * Update storage in backend.
 *
 * @param {Invoice[]} [updates]
 * @returns {ArrayMap<Invoice>}
 */
function updateStorageNoReturn(updates?: Invoice[]): void {
	if (logging) console.debug('⮂ invoiceReducer: updateStorageNoReturn');
	
	const firmId = locals.getString(locals.USER_FIRM_ID, '');
	const userId = locals.getString(locals.USER_ID, '');

	if (updates && updates.length > 0) callStorageWorker(null, firmId, userId, updates);
}

/**
 * Create state with optional data.
 *
 * @param {Invoice[]} [data]
 * @returns {ArrayMap<Invoice>}
 */
function createState(data?: Invoice[] | ArrayMap<Invoice>): ArrayMap<Invoice> {
	if (!data) return ArrayMapRegistry.clone<Invoice>('invoice', []);
    else if (data instanceof ArrayMap) return ArrayMapRegistry.clone<Invoice>('invoice', data.map(e => e));
    else return ArrayMapRegistry.clone<Invoice>('invoice', data);
}

function invoices(state: ArrayMap<Invoice>, action: InvoiceAction): ArrayMap<Invoice> {
	if (!state) state = createState();

	let st: ArrayMap<Invoice>;

	switch (action.type) {
		// action.invoice - the new value of invoice with id action.id
		// action.invoiceId - id of the target, used if new value is null
		case Actions.INVOICE_FETCH_ONE_SUCCESS:
			/* TODO: This is busted for all but modifications! */
			if (logging) console.debug('⮂ invoiceReducer: FETCH_ONE_SUCCESS', action);

			if (action.invoice === null) return state;

			// The addedOn date of the existing one will always be greater than the one passed through
			// (Because it was updated!)
			if (state.has(action.invoiceId)) {
				st = state.clone();
				st.set(action.invoice);
				// state.clear();

				updateStorageNoReturn([action.invoice]);
				return st;
			}
			else return state;

		/* Handle incoming fetch:
			 partial, stateEmpty, fetchEmpty		-> A do nothing, return state
			 partial, stateEmpty, !fetchEmpty	 -> B return action.invoices
			 partial, !stateEmpty, fetchEmpty	 -> A do nothing, return state
			 partial, !stateEmpty, !fetchEmpty	-> C update/add entries in action.invoices
			 !partial, stateEmpty, fetchEmpty	 -> A do nothing, return state
			 !partial, stateEmpty, !fetchEmpty	-> B return action.invoices
			 !partial, !stateEmpty, fetchEmpty	-> D return invoices in state newer than last fetch time
			 !partial, !stateEmpty, !fetchEmpty -> E merge invoices in state newer than those in action.invoices
		 */
		case Actions.INVOICE_FETCH_SUCCESS:
			const partial = action.partial;
			const lastFetch = action.lastFetch;
			const stateEmpty = state.length === 0;
			const fetchEmpty = action.invoices.length === 0;
			const fetched = action.invoices.map(invoice => new Invoice({...invoice, persisted: true}));

			if (logging) console.debug(`⮂ invoiceReducer: ${partial ? 'partial' : 'full'} fetch of ${fetched.length} invoices with ${stateEmpty ? 'empty' : 'non-empty'} state and ${fetchEmpty ? 'empty' : 'non-empty'} fetch`);

			// ✓ We didn't have anything in our state, so whatever is in fetched is the new state
			const replaceState = () => {
				if (logging) console.debug('⮂ invoiceReducer: replaceState', {partial, stateEmpty, fetchEmpty, lastFetch});

				if (fetched.length) {
					// Create a new state based on the fetched results.
					st = createState(fetched);

					return updateStorage(st);
				}
				else return state;
			}

			// ✓ Partial fetch with result and state was not empty, update state with fetched result.
			//	 Bear in mind that partial fetches includes deletes for all entities, so we need to use that
			//	 information to delete from current state if status of the fetched result is 'D'.
			const partialMerge = () => {
				if (logging) console.debug('⮂ invoiceReducer: partialMerge', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Use a fresh copy of the state
				const st: ArrayMap<Invoice> = state.clone();

				// We only updateStorage if there is something to update
				const toUpdate: Invoice[] = [];

				// For each item, add it if it doesn't exist, replace if it does, and check for deleted
				fetched.forEach(invoice => {
					const current = st.get(invoice.id);

					if (current) {
						/* Has it been deleted?
						 -- Note that partials include deleted for everything!
						 -- Note that some tables retain the deleted, and some do not.
						 -- We need to do the right thing here
						*/
						if (current.status !== 'D' && invoice.status === 'D') {
							// Remove it from state
							toUpdate.push(invoice);
							st.delete(invoice);
						}

						/* Not deleted */
						else {
							// Is it newer than what we have? Update the backups and set it locally
							if (invoice.addedOn > current.addedOn) {
								toUpdate.push(invoice);
								st.set(invoice);
							}
						}
					}

					// Otherwise add it (but not if it's deleted — no point adding a deleted entry we never had)
					else {
						if (invoice.status !== 'D') {
							toUpdate.push(invoice);
							st.set(invoice);
						}
						else if (logging) console.debug('⮂ invoiceReducer: partialMerge: skipping deleted invoice not in state');
					}
				});

				return updateStorage(st, toUpdate);
			};

			// ✓ Full dataset with no data returned but existing state: return empty set.
			const empty = () => {
				if (logging) console.debug('⮂ invoiceReducer: empty: invoices', {partial, stateEmpty, fetchEmpty, lastFetch});

				// Clone and clear to produce an empty state with the same schema
				st = state.clone();
				st.clear();

				return updateStorage(st);
			};

			// ✓ Full dataset returned with entries but existing state, must merge.
			//	 The lastFetch variable will have the time (hrt) we started the fetch, which we can use to determine
			//	 which to use, state or fetched.
			//	 For each fetched result, we need to find the matching entry in state.
			//	 Then we need to double-check that the state has not been updated since the last save.
			const fullMerge = () => {
				if (logging) console.debug('⮂ invoiceReducer: fullMerge: invoices', {partial, stateEmpty, fetchEmpty, lastFetch});

				st = createState(fetched); // Start with the new stuff

				// Fetched represents the entire dataset, so we just need to update the fetched results with anything not yet saved.
                state.forEach(localInvoice => {
                    // If this invoice has not yet been saved, the remote state may not know about it.
                    // BUT the persisted flag is unreliable (batch saves stamp it false), so a stale
                    // !persisted local must NOT shadow a newer authoritative server row (OWA-7 #1).
                    // Only re-apply a !persisted local when it is absent from the fetched set OR is
                    // strictly newer by addedOn than the fetched row for the same id. Otherwise the
                    // fetched (server) row wins.
                    if (!localInvoice.persisted) {
                        const fetchedInvoice = st.get(localInvoice.id);
                        if (!fetchedInvoice || localInvoice.addedOn > fetchedInvoice.addedOn) st.set(localInvoice);
                    }
                });

				return updateStorage(st);
			};

			if (partial) {										// ✓ Fetched results are only things changed since last check
				if (stateEmpty) {								// ✓ Partial fetch with nothing in state
					if (fetchEmpty) return state; 	// ✓ Partial fetch with no results and nothing in state just return state
					else return replaceState();			// ✓ Partial fetch with some results, but nothing in state, so this is the new state
				}
				else {											// ✓ There are existing entries in state
					if (fetchEmpty) return state;				// ✓ Partial fetch with no results, nothing to do but return state
					else return partialMerge(); 	// ✓ Partial fetch with result and state was not empty, update state with fetched result
				}
			}
			else {												// ✓ Full dataset returned
				if (stateEmpty) {								// ✓ We are getting back the full result set
					if (fetchEmpty) return state; 	// ✓ Full dataset with no data, but state was empty anyway, just return state
					else return replaceState();			// ✓ Full dataset returned with data, so this is new state
				}
				else {											// ✓ Full dataset returned but there is some data in state
					if (fetchEmpty) return empty();				// ✓ Full dataset with no data returned but existing state: return empty set.
					else return fullMerge();		// ✓ Full dataset returned with entries but existing state, must merge
				}
			}


		case Actions.INVOICE_FETCH_ONE_FAILURE:
			console.error('INVOICE_FETCH_ONE_FAILURE', action.invoiceId, action.err);

			// No update to storage required
			return state;

		case Actions.INVOICE_FETCH_FAILURE:
			console.error('INVOICE_FETCH_FAILURE', action.invoiceId, action.err);

			// No update to storage required
			return state;

		case Actions.INVOICE_RESTORE: {
		    let invoice = new Invoice({...action.invoice});
            st = state.clone();

            const existing = st.get(invoice.id);

            if (existing) {
                // Only update if the incoming version is at least as new
                if (invoice.addedOn >= existing.addedOn) {
                    st.set(invoice);
                    return updateStorage(st, [invoice]);
                }
                else return state;
            }
            else {
                // Don't add deleted entries that aren't already in state
                if (invoice.status === 'D') return state;

                st.set(invoice);
                return updateStorage(st, [invoice]);
            }
        }
            
		case Actions.INVOICE_SAVE: {
		    // console.debug('⮂ INVOICE_SAVE reducer', action.invoice?.id);
		    
			// clone the invoice
			let invoice = new Invoice({...action.invoice});

			st = state.clone();
			// state.clear(); 
			
			
			// Add or update the new invoice in the state
			// If this is a create, add to the queue
			const _invoice = st.get(invoice.id);

			if (!_invoice) st.set(invoice);
			else {
				if (invoice.addedOn >= _invoice.addedOn) st.set(invoice);
			}

			return updateStorage(st, [invoice]);
		}

		case Actions.INVOICE_SAVE_MANY: {
		    if (logging) console.debug('⮂ INVOICE_SAVE_MANY reducer', action.invoices.map(invoice => invoice.id).join(','));
		    
			// clone the invoices
			let toProcess = action.invoices.map(invoice => new Invoice(invoice));

			st = state.clone();
			// state.clear();

			const updates: Invoice[] = [];

			toProcess.forEach(invoice => {
				// Add or update the new invoice in the state
				// If this is a create, add to the queue
				const existing = st.get(invoice.id);

				if (!existing) {
					// Don't add deleted entries that aren't already in state
					if (invoice.status !== 'D') {
						st.push(invoice);
						updates.push(invoice);
					}
					else if (logging) console.debug('⮂ INVOICE_SAVE_MANY: skipping deleted invoice not in state');
				}
				else {
					if (invoice.addedOn >= existing.addedOn) {
						st.set(invoice);
						updates.push(invoice);
					}
					else if (logging) console.debug('⮂ Skipping, existing invoice had newer date.')
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.INVOICE_REMOVE: {
			// clone the invoice
			const invoice = new Invoice({...action.invoice});

			// Remove from UI
			st = state.clone();
			// state.clear();

			st.delete(invoice);

			return updateStorage(st, [invoice]);
		}

		case Actions.INVOICE_RESTORE_FAILURE:
		case Actions.INVOICE_SAVE_FAILURE: {
		    console.warn('INVOICE_SAVE_FAILURE reducer', action.invoice?.id);
		    if (!action.invoice?.id) {
				console.warn('INVOICE_SAVE_FAILURE reducer', action.err);
				return state;
		    }
			
			const invoice = ensure(Invoice, action.invoice);
			if (!invoice) {
				console.error('Could not find invoice after call to save');
				return state;
			}

			st = state.clone();

			const _invoiceRef = state.get(invoice.id);

			if (_invoiceRef && (_invoiceRef.addedOn === invoice.addedOn)) {
				// Create a new instance to avoid mutating the entity shared with old state
				const _invoice = new Invoice({..._invoiceRef});
				_invoice.fatalError = {
					status: action.status,
					message: action.message,
					recoverable: isRecoverableError(action.status, action.message)
				};
				_invoice.persisted = true;	// Not really persisted, but can't keep it blue

				// Update the queue with the new info, so that we can filter these out in the queues
				idb.add('invoice', _invoice).then(r => {
						if (r.isErr()) console.error(`⮂ invoiceReducer: failed to save invoice with fatalError info: ${r.unwrapErr().message}`);
						else console.debug('⮂ invoiceReducer: saved invoice with fatalError info');
					}).catch(err => console.error(`⮂ invoiceReducer: failed to save invoice with fatalError info: ${err.message}`));

				st.set(_invoice);
			}

			return updateStorage(st);
		}

		case Actions.INVOICE_SAVE_MANY_FAILURE: {
		    console.warn('INVOICE_SAVE_MANY_FAILURE reducer', action.invoices.map(invoice => invoice.id).join(','));
		    
			// NOT IMPLEMENTED
			return state;
		}

		case Actions.INVOICE_RESTORE_SUCCESS:
		case Actions.INVOICE_SAVE_SUCCESS: {
			if (!action.invoice?.id) {
				console.warn('INVOICE_SAVE_SUCCESS reducer');
                return state;
			}

			const invoiceRef = state.get(action.invoice?.id);
			if (!invoiceRef) {
				console.warn('Could not find invoice after call to save');
                return state;
			}

			st = state.clone();

			if (action.invoice.addedOn >= invoiceRef.addedOn) {
				st.set(action.invoice);
			}
			else {
				// Create a new instance to avoid mutating the entity shared with old state
				const invoice = new Invoice({...invoiceRef, persisted: true});
				st.set(invoice);
			}

			return updateStorage(st, [action.invoice]);
		}

		case Actions.INVOICE_SAVE_MANY_SUCCESS: {
		    // console.debug('⮂ INVOICE_SAVE_MANY_SUCCESS reducer', action.invoices.map(invoice => invoice.id).join(','));

			// We need to update all of the saved items, then push the items to updateStorage
			st = state.clone();

			const invoices: Invoice[] = [];

			action.invoices.forEach(e => {
				const invoiceRef = st.get(e.id);

                // If we can't find it, it was probably deleted
				if (!invoiceRef) console.warn('Could not find invoice after call to save');
				else if (e.addedOn >= invoiceRef.addedOn) {
				    st.set(e);
				    invoices.push(e);
                }
				else {
					// Create a new instance to avoid mutating the entity shared with old state
					const invoice = new Invoice({...invoiceRef, persisted: true});
					st.set(invoice);
					invoices.push(invoice);
				}
			});

			return updateStorage(st, invoices);
		}

		case Actions.INVOICE_REMOVE_FAILURE: {
		    console.warn('INVOICE_REMOVE_FAILURE action', action.invoice?.id);
		    
			// No storage update needed
			return state;
		}

		case Actions.INVOICE_REMOVE_SUCCESS: {
			// No storage update needed
			return state;
		}

		case Actions.INVOICE_RESTORE_MANY: {
			if (logging) console.debug('⮂ INVOICE_RESTORE_MANY reducer', action.invoices.map(invoice => invoice.id).join(','));

			const toProcess = action.invoices.map(invoice => new Invoice({...invoice}));
			st = state.clone();

			const updates: Invoice[] = [];

			toProcess.forEach(invoice => {
				const existing = st.get(invoice.id);

				if (existing) {
					// Only update if the incoming version is at least as new
					if (invoice.addedOn >= existing.addedOn) {
						st.set(invoice);
						updates.push(invoice);
					}
					else if (logging) console.debug('⮂ INVOICE_RESTORE_MANY: skipping older version of invoice');
				}
				else {
					// Don't add deleted entries that aren't already in state
					if (invoice.status !== 'D') {
						st.set(invoice);
						updates.push(invoice);
					}
					else if (logging) console.debug('⮂ INVOICE_RESTORE_MANY: skipping deleted invoice not in state');
				}
			});

			return updateStorage(st, updates);
		}

		case Actions.INVOICE_REMOVE_MANY: {
			if (logging) console.debug('⮂ INVOICE_REMOVE_MANY reducer', action.invoices.map(invoice => invoice.id).join(','));

			const toProcess = action.invoices.map(invoice => new Invoice({...invoice}));
			st = state.clone();

			toProcess.forEach(invoice => {
				st.delete(invoice);
			});

			return updateStorage(st, toProcess);
		}

		default:
			// No storage update needed
			return state;
	}
}

export default invoices;